import { useUserContext } from 'providers/UserProvider';
import { userHasSomeRoles } from 'services/Authorization';
import { Roles } from 'UI/constants/roles';
import { AdditionalRecruiterStatus, AdditionalRecruiterType } from 'UI/constants/status';

export const SUPER_USER_ROLES = [
  Roles.Operations,
  Roles.ProductionDirector,
  Roles.Leadership,
  Roles.DataCoordinator
];

const normalizeMainRecruiter = ({ recruiter, team }) => {
  if (!recruiter) return null;
  const { id, email, personalInformation, isOffice } = recruiter;

  const normalizedRecruiter = {
    id,
    email,
    fullName: personalInformation?.full_name,
    isOffice,
    team: normalizeMainTeam(team),
    type: AdditionalRecruiterType.Accountable
  };

  return normalizedRecruiter;
};

const normalizeAdditionalRecruiters = additionalRecruiters => {
  return (additionalRecruiters || [])
    .filter(({ status }) => status === AdditionalRecruiterStatus.Approved)
    .map(
      ({
        recruiter,
        coach: team,
        isOffice,
        type,
        recruiter_to_collaborate_id: recruiterToCollaborateId
      }) => {
        const { id, email, personalInformation } = recruiter || {};

        return {
          id,
          team,
          email,
          fullName: personalInformation?.full_name,
          isOffice,
          recruiterToCollaborateId,
          type
        };
      }
    );
};

const normalizeMainTeam = team => {
  if (!team) return null;
  const { id, email, full_name: fullName, email_team: teamEmail } = team;

  const normalizedTeam = {
    id,
    email,
    fullName,
    teamEmail
  };

  return normalizedTeam;
};

export const useSendoutAccountability = ({
  recruiter,
  coach: team,
  additionalRecruiters = []
} = {}) => {
  const [currentUser] = useUserContext();

  const mainRecruiter = normalizeMainRecruiter({ recruiter, team });
  const allRecruiters = recruiter
    ? [mainRecruiter, ...normalizeAdditionalRecruiters(additionalRecruiters)]
    : [];

  const accountables = allRecruiters.filter(
    ({ type }) => type === AdditionalRecruiterType.Accountable
  );
  const collaborators = allRecruiters.filter(
    ({ type }) => type === AdditionalRecruiterType.Collaborator
  );
  const currentUserId = currentUser?.id ?? null;
  const currentUserCoachId = currentUser?.teamRelation?.coachId ?? null;

  const isUserAccountable = accountables.some(accountable => accountable.id === currentUserId);
  const isUserCollaborator = collaborators.some(accountable => accountable.id === currentUserId);

  const isUserSuperUser = userHasSomeRoles(currentUser, SUPER_USER_ROLES);
  const isUserAccountableTeamCoach =
    !!currentUserId && accountables.some(accountable => accountable.team?.id === currentUserId);
  const isUserAccountableTeamDirector =
    userHasSomeRoles(currentUser, [Roles.AssistantRegionalDirector]) &&
    !!currentUserCoachId &&
    accountables.some(accountable => accountable.team?.id === currentUserCoachId);

  const canUserCreateSendout =
    isUserAccountable ||
    isUserCollaborator ||
    isUserAccountableTeamCoach ||
    isUserAccountableTeamDirector ||
    isUserSuperUser;

  return {
    isUserAccountable,
    isUserCollaborator,
    isUserSuperUser,
    isUserAccountableTeamCoach,
    isUserAccountableTeamDirector,
    canUserCreateSendout
  };
};
